/**
 * 牌局战报：房主「结束牌局」之后所有人看到的那一屏。
 *
 * ── 它要能被截图发到群里 ─────────────────────────────────────
 * 这是整个产品里唯一会被主动分享的一屏。所以：一屏放得下 6 人（440×856 实测）、
 * 不横向滚动、金额一律 `tabular-nums` 对齐、按钮不压在数字上。
 *
 * ── 正负必须双重编码 ────────────────────────────────────────
 * 净胜负同时用**符号**（+ / −）和颜色表达（§11.10）。只靠红绿的话，
 * 色觉障碍的人看到的就是一列分不出输赢的数字 —— 而这一列正是全屏最关键的信息。
 *
 * ── 合计那行是筹码守恒的校验，不是装饰 ────────────────────────
 * `Σ买入 == Σ最终筹码`（§8.9）。`balanced === false` 意味着**有 bug**，
 * 这时必须显眼示警并说清「这是程序的问题」：让人对着一份对不上的账互相猜疑，
 * 比直接承认算错了糟糕得多。
 *
 * ── 结束牌局是不可逆操作 ────────────────────────────────────
 * 所以入口藏在房间面板里，且要二次确认。
 */
import { useCallback } from 'react'
import type { ReactNode } from 'react'
import type { SettlementReport } from '@poker/shared'

import { useModalDialog } from '../hooks/useModalDialog.js'
import { useOpenTapGuard } from '../hooks/useOpenTapGuard.js'
import { IconCheck } from '../icons/index.js'
import { useUiStore } from '../store/index.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'

import { buildSettlementModel, settlementOf, type SettlementLine } from './topUpDerive.js'
import { useRoomLink } from './roomLink.js'
import './room.css'

// ============================================================================
// 战报
// ============================================================================

export interface SettlementOverlayProps {
  /** 回大厅。战报是这局的终点，必须给一条明确的出路。 */
  readonly onLeave?: () => void
  /** 覆盖 store 里的战报（回放 / storybook / 单测用）；省略就订阅 store。 */
  readonly report?: SettlementReport | null
}

export function SettlementOverlay({ onLeave, report: reportProp }: SettlementOverlayProps = {}): ReactNode {
  const view = useNetStore(selectNetView)
  const report = reportProp === undefined ? settlementOf(view) : reportProp
  const open = report !== null
  const { dialogRef, onDialogKeyDown } = useModalDialog({ open })
  if (report === null) return null

  const model = buildSettlementModel(report)

  return (
    <div className="settle" role="dialog" aria-modal="true" aria-label={model.headline}>
      <div className="settle__card" ref={dialogRef} tabIndex={-1} onKeyDown={onDialogKeyDown}>
        <header className="settle__head">
          <div className="settle__brand" translate="no">GÖDEL</div>
          <span className="settle__edition">THE FINAL RECORD · 私人牌室</span>
          <h2 className="settle__title">牌局结束</h2>
          <p className="settle__meta num">{model.meta}</p>
        </header>
        <div className="settle__intro"><span>这一局，尽兴而归。</span><small>筹码有输赢，交情不散场。</small></div>

        <div className="settle__table">
          <div className="settle__row settle__row--head">
            <span className="settle__who">玩家</span>
            <span className="settle__cell">买入</span>
            <span className="settle__cell">最终</span>
            <span className="settle__cell">净胜负</span>
          </div>

          {model.rows.map((r) => (
            <SettlementRowView key={r.key} row={r} />
          ))}

          <div className="settle__row settle__row--total">
            <span className="settle__who">合计</span>
            <span className="settle__cell num">{model.totals.buyIn}</span>
            <span className="settle__cell num">{model.totals.finalStack}</span>
            <span className={`settle__cell num settle__net settle__net--${model.totals.tone}`}>
              {model.totals.net}
              {model.balanced && <IconCheck size={14} title="筹码对得上" />}
            </span>
          </div>
        </div>
        <p className="settle__receipt">{model.balanced ? '账目已核对 · 买入含起始筹码与追加筹码' : '账目异常 · 请核对下方提示'}</p>

        {/* 不守恒 = 程序算错了。绝不静默展示一份对不上的账 */}
        {!model.balanced && (
          <p className="settle__warn" role="alert">
            筹码对不上，这份账是错的。程序出了问题，不是谁少算了。
          </p>
        )}

        {onLeave !== undefined && (
          <button type="button" className="btn btn--cta pressable settle__done" onClick={onLeave}>
            回大厅
          </button>
        )}
      </div>
    </div>
  )
}

function SettlementRowView({ row }: { readonly row: SettlementLine }): ReactNode {
  return (
    <div className={row.left ? 'settle__row settle__row--left' : 'settle__row'}>
      <span className="settle__who">
        <span className="settle__name">{row.nickname}</span>
        {/* ★ 补买次数只在 > 0 时出现：给每一行都挂个「补买 0 次」是在制造噪音 */}
        {row.topUpCount > 0 && (
          <span className="settle__tag num">补买 {row.topUpCount} 次</span>
        )}
        {row.left && <span className="settle__tag">已离座</span>}
      </span>
      <span className="settle__cell num">{row.buyIn}</span>
      <span className="settle__cell num">{row.finalStack}</span>
      <span className={`settle__cell num settle__net settle__net--${row.tone}`}>{row.net}</span>
    </div>
  )
}

// ============================================================================
// 结束牌局
// ============================================================================

/** 房间面板里的入口。房主专用，且只是打开确认面板，不直接结束。 */
export function EndGameButton(): ReactNode {
  const openSheet = useUiStore((s) => s.openSheet)
  return (
    <button type="button" className="btn btn--quiet settle__end" onClick={() => openSheet('endGame')}>
      结束牌局
    </button>
  )
}

/**
 * 二次确认。
 *
 * 为什么这一次用模态而不是像「离开」那样的两段式按钮：结束牌局作用于**所有人**，
 * 而且不可逆 —— 它不属于「点错了大不了再点回来」那一类。
 * 与审批不同，这一步是房主自己主动点进来的，挡住牌桌没有任何代价。
 */
export function EndGameSheet(): ReactNode {
  const sheet = useUiStore((s) => s.sheet)
  const closeSheet = useUiStore((s) => s.closeSheet)
  const link = useRoomLink()
  const open = sheet === 'endGame'
  const { dialogRef, onDialogKeyDown } = useModalDialog({ open, onClose: closeSheet })
  // 结束牌局不可逆：双击入口的第二下不许直接落在确认键上
  const guardTap = useOpenTapGuard(open)

  const end = useCallback((): void => {
    link.send({ type: 'endGame' })
    closeSheet()
  }, [link, closeSheet])

  if (!open) return null

  return (
    <div className="sheet-scrim sheet-scrim--paper" onClickCapture={guardTap} onClick={closeSheet} role="presentation">
      <div
        className="sheet"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="结束牌局"
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet__head">
          <strong>结束牌局</strong>
        </header>
        {/* 不可逆操作必须说清后果（COPY.md「什么必须保留」）——但只说一句 */}
        <p className="sheet__empty">结算之后这个房间就关了，所有人会看到战报。</p>
        <button type="button" className="btn btn--cta pressable" onClick={end}>
          结算并结束
        </button>
        <button type="button" className="btn btn--ghost sheet__close" onClick={closeSheet}>
          再打一会
        </button>
      </div>
    </div>
  )
}

export default SettlementOverlay
